import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandName } from "@/components/Logo";
import { LegalFooter } from "@/components/LegalFooter";

export function AuthShell({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <div className="grid min-h-screen bg-[#FAF7F1] lg:grid-cols-[1fr_1.1fr]">
      <aside className="relative hidden overflow-hidden border-r border-[#CBB898] bg-[#ECE3D3] p-12 text-[#1A1F2B] lg:flex lg:flex-col lg:justify-between">
        <div className="relative flex items-center gap-3">
          <BrandName />
        </div>
        <div className="relative max-w-md">
          <p className="screen-title leading-tight">Tu dinero, con estrategia.</p>
          <div className="mt-10 flex gap-8 text-sm">
            <div><p className="font-semibold text-[#1B6E4F]">+ Ingresos</p><p className="mt-1 text-[#5A6170]">lo que entra</p></div>
            <div><p className="font-semibold text-[#B23A1E]">− Gastos</p><p className="mt-1 text-[#5A6170]">lo que sale</p></div>
            <div><p className="font-semibold text-[#2C4F91]">→ Ahorro</p><p className="mt-1 text-[#5A6170]">tu futuro</p></div>
          </div>
        </div>
        <p className="relative text-[13px] text-[#5A6170]">Finanzas personales para México · MXN</p>
      </aside>
      <main className="flex items-center justify-center px-5 py-12 sm:px-10">
        <div className="fade-up w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <BrandName />
          </div>
          <h1 className="screen-title">{title}</h1>
          <p className="mt-2 text-sm text-slate-500 sm:text-base">{subtitle}</p>
          <div className="mt-8 rounded-[24px] border border-[#E7DECF] bg-white p-6 lg:p-8">{children}</div>
          <LegalFooter className="mt-6 justify-center" />
        </div>
      </main>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  extra?: ReactNode;
}

export function AuthField({ id, label, type = "text", value, onChange, autoComplete, extra }: FieldProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
        {extra}
      </div>
      <Input
        id={id} type={type} value={value} autoComplete={autoComplete} required
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        className="h-12 bg-white focus-visible:ring-[#2C4F91]"
        data-testid={`${id}-input`}
      />
    </div>
  );
}

export function FormError({ message, testId }: { message: string; testId: string }) {
  if (!message) return null;
  return (
    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid={testId}>{message}</p>
  );
}
